import React, { useState, useEffect } from "react";
import {
  listSeasons,
  getSeasonStats,
  getCareerStats,
  type LeagueSeason,
  type LeaguePlayerSeasonStats,
} from "../../store/leagueSeasons";
import type { LeagueTeam } from "../../store/leagues";

interface Props {
  leagueId: string;
  teams: LeagueTeam[];
}

type StatsMode = "season" | "career";
type StatsCategory = "batting" | "bowling" | "fielding" | "awards";

export default function LeagueStatsTab({ leagueId, teams }: Props) {
  const [seasons, setSeasons] = useState<LeagueSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<LeagueSeason | null>(null);
  const [stats, setStats] = useState<LeaguePlayerSeasonStats[]>([]);
  const [mode, setMode] = useState<StatsMode>("season");
  const [category, setCategory] = useState<StatsCategory>("batting");
  const [filterTeamId, setFilterTeamId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSeasons();
  }, [leagueId]);

  useEffect(() => {
    loadStats();
  }, [selectedSeason, mode, filterTeamId]);

  async function loadSeasons() {
    const data = await listSeasons(leagueId);
    setSeasons(data);
    if (data.length > 0) {
      setSelectedSeason(data.find(s => s.is_active) || data[0]);
    }
    setLoading(false);
  }

  async function loadStats() {
    if (mode === "career") {
      const data = await getCareerStats(leagueId);
      setStats(data as any);
    } else if (selectedSeason) {
      const data = await getSeasonStats(selectedSeason.id, filterTeamId || undefined);
      setStats(data);
    }
  }

  const filteredStats = stats.filter(s => {
    if (searchQuery) {
      return s.player_name.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const sortedStats = [...filteredStats].sort((a, b) => {
    if (category === "batting") return b.bat_runs - a.bat_runs;
    if (category === "bowling") return b.bowl_wickets - a.bowl_wickets;
    if (category === "fielding") return (b.field_catches + b.field_run_outs + b.field_stumpings) - (a.field_catches + a.field_run_outs + a.field_stumpings);
    if (category === "awards") return b.player_of_match - a.player_of_match;
    return 0;
  });

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-10 bg-[#0b5c33]/30 rounded-lg" />
        <div className="h-64 bg-[#0b5c33]/20 rounded-xl" />
      </div>
    );
  }

  if (seasons.length === 0) {
    return (
      <div className="bg-[#064428]/30 rounded-xl p-10 text-center border border-[#0b5c33]/30">
        <p className="text-green-300/60 text-sm">No seasons created yet. Create a season and complete matches to see stats.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-2">
        {/* Season/Career toggle */}
        <div className="flex bg-[#0b5c33]/30 rounded-lg p-0.5">
          <button
            onClick={() => setMode("season")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${mode === "season" ? "bg-[#0f9d3d] text-white" : "text-green-300/60 hover:text-green-300"}`}
          >
            Season
          </button>
          <button
            onClick={() => setMode("career")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${mode === "career" ? "bg-[#0f9d3d] text-white" : "text-green-300/60 hover:text-green-300"}`}
          >
            Career
          </button>
        </div>

        {mode === "season" && (
          <select
            value={selectedSeason?.id || ""}
            onChange={(e) => setSelectedSeason(seasons.find(s => s.id === e.target.value) || null)}
            className="bg-[#0b5c33]/40 border border-[#0b5c33] text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]"
          >
            {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}

        <select
          value={filterTeamId}
          onChange={(e) => setFilterTeamId(e.target.value)}
          className="bg-[#0b5c33]/40 border border-[#0b5c33] text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]"
        >
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
        </select>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search player..."
          className="bg-[#0b5c33]/40 border border-[#0b5c33] text-white rounded-lg px-3 py-1.5 text-xs placeholder-green-600 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d] min-w-[120px]"
        />
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 overflow-x-auto">
        {(["batting", "bowling", "fielding", "awards"] as StatsCategory[]).map(cat => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-colors ${
              category === cat ? "bg-[#0b5c33]/60 text-white" : "text-green-300/50 hover:text-green-300 hover:bg-[#0b5c33]/20"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Stats table */}
      {sortedStats.length === 0 ? (
        <div className="bg-[#064428]/30 rounded-xl p-8 text-center border border-[#0b5c33]/30">
          <p className="text-green-300/60 text-sm">No stats available. Complete league matches to generate statistics.</p>
        </div>
      ) : (
        <div className="bg-[#064428]/40 rounded-xl border border-[#0b5c33]/50 overflow-hidden">
          <div className="overflow-x-auto">
            {category === "batting" && <BattingTable stats={sortedStats} />}
            {category === "bowling" && <BowlingTable stats={sortedStats} />}
            {category === "fielding" && <FieldingTable stats={sortedStats} />}
            {category === "awards" && <AwardsTable stats={sortedStats} />}
          </div>
        </div>
      )}
    </div>
  );
}

function BattingTable({ stats }: { stats: LeaguePlayerSeasonStats[] }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-[#0b5c33]/40">
        <tr className="text-green-300/70">
          <th className="px-3 py-2.5 text-left font-semibold">#</th>
          <th className="px-3 py-2.5 text-left font-semibold">Player</th>
          <th className="px-2 py-2.5 text-center font-semibold">M</th>
          <th className="px-2 py-2.5 text-center font-semibold">I</th>
          <th className="px-2 py-2.5 text-center font-semibold">NO</th>
          <th className="px-2 py-2.5 text-center font-semibold">R</th>
          <th className="px-2 py-2.5 text-center font-semibold">HS</th>
          <th className="px-2 py-2.5 text-center font-semibold">Avg</th>
          <th className="px-2 py-2.5 text-center font-semibold">SR</th>
          <th className="px-2 py-2.5 text-center font-semibold">4s</th>
          <th className="px-2 py-2.5 text-center font-semibold">6s</th>
          <th className="px-2 py-2.5 text-center font-semibold">50</th>
          <th className="px-2 py-2.5 text-center font-semibold">100</th>
        </tr>
      </thead>
      <tbody>
        {stats.map((s, i) => (
          <tr key={s.id || i} className="border-t border-[#0b5c33]/20 hover:bg-[#0b5c33]/10">
            <td className={`px-3 py-2 font-bold ${i === 0 ? "text-yellow-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-300" : "text-green-400"}`}>{i + 1}</td>
            <td className="px-3 py-2 text-white font-medium truncate max-w-[100px]">{s.player_name}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_matches}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_innings}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_not_outs}</td>
            <td className="px-2 py-2 text-center text-white font-semibold">{s.bat_runs}</td>
            <td className="px-2 py-2 text-center text-green-300">{s.bat_highest}{s.bat_highest_not_out ? "*" : ""}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_average.toFixed(1)}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_strike_rate.toFixed(1)}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_fours}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_sixes}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_fifties}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bat_hundreds}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BowlingTable({ stats }: { stats: LeaguePlayerSeasonStats[] }) {
  const bowlers = stats.filter(s => s.bowl_balls > 0).sort((a, b) => b.bowl_wickets - a.bowl_wickets);
  return (
    <table className="w-full text-xs">
      <thead className="bg-[#0b5c33]/40">
        <tr className="text-green-300/70">
          <th className="px-3 py-2.5 text-left font-semibold">#</th>
          <th className="px-3 py-2.5 text-left font-semibold">Player</th>
          <th className="px-2 py-2.5 text-center font-semibold">I</th>
          <th className="px-2 py-2.5 text-center font-semibold">O</th>
          <th className="px-2 py-2.5 text-center font-semibold">M</th>
          <th className="px-2 py-2.5 text-center font-semibold">R</th>
          <th className="px-2 py-2.5 text-center font-semibold">W</th>
          <th className="px-2 py-2.5 text-center font-semibold">Econ</th>
          <th className="px-2 py-2.5 text-center font-semibold">Avg</th>
          <th className="px-2 py-2.5 text-center font-semibold">SR</th>
          <th className="px-2 py-2.5 text-center font-semibold">Best</th>
        </tr>
      </thead>
      <tbody>
        {bowlers.map((s, i) => (
          <tr key={s.id || i} className="border-t border-[#0b5c33]/20 hover:bg-[#0b5c33]/10">
            <td className={`px-3 py-2 font-bold ${i === 0 ? "text-yellow-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-300" : "text-green-400"}`}>{i + 1}</td>
            <td className="px-3 py-2 text-white font-medium truncate max-w-[100px]">{s.player_name}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_innings}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_overs}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_maidens}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_runs}</td>
            <td className="px-2 py-2 text-center text-white font-semibold">{s.bowl_wickets}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_economy.toFixed(1)}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_average > 0 ? s.bowl_average.toFixed(1) : "-"}</td>
            <td className="px-2 py-2 text-center text-green-300/70">{s.bowl_strike_rate > 0 ? s.bowl_strike_rate.toFixed(1) : "-"}</td>
            <td className="px-2 py-2 text-center text-green-300">{s.bowl_best_wickets}/{s.bowl_best_runs}</td>
          </tr>
        ))}
        {bowlers.length === 0 && (
          <tr><td colSpan={11} className="px-4 py-6 text-center text-green-300/50">No bowling stats available</td></tr>
        )}
      </tbody>
    </table>
  );
}

function FieldingTable({ stats }: { stats: LeaguePlayerSeasonStats[] }) {
  const fielders = stats.filter(s => s.field_catches + s.field_run_outs + s.field_stumpings > 0)
    .sort((a, b) => (b.field_catches + b.field_run_outs + b.field_stumpings) - (a.field_catches + a.field_run_outs + a.field_stumpings));
  return (
    <table className="w-full text-xs">
      <thead className="bg-[#0b5c33]/40">
        <tr className="text-green-300/70">
          <th className="px-3 py-2.5 text-left font-semibold">#</th>
          <th className="px-3 py-2.5 text-left font-semibold">Player</th>
          <th className="px-3 py-2.5 text-center font-semibold">Catches</th>
          <th className="px-3 py-2.5 text-center font-semibold">Run Outs</th>
          <th className="px-3 py-2.5 text-center font-semibold">Stumpings</th>
          <th className="px-3 py-2.5 text-center font-semibold">Total</th>
        </tr>
      </thead>
      <tbody>
        {fielders.map((s, i) => (
          <tr key={s.id || i} className="border-t border-[#0b5c33]/20 hover:bg-[#0b5c33]/10">
            <td className={`px-3 py-2 font-bold ${i === 0 ? "text-yellow-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-300" : "text-green-400"}`}>{i + 1}</td>
            <td className="px-3 py-2 text-white font-medium">{s.player_name}</td>
            <td className="px-3 py-2 text-center text-green-300/70">{s.field_catches}</td>
            <td className="px-3 py-2 text-center text-green-300/70">{s.field_run_outs}</td>
            <td className="px-3 py-2 text-center text-green-300/70">{s.field_stumpings}</td>
            <td className="px-3 py-2 text-center text-white font-semibold">{s.field_catches + s.field_run_outs + s.field_stumpings}</td>
          </tr>
        ))}
        {fielders.length === 0 && (
          <tr><td colSpan={6} className="px-4 py-6 text-center text-green-300/50">No fielding stats available</td></tr>
        )}
      </tbody>
    </table>
  );
}

function AwardsTable({ stats }: { stats: LeaguePlayerSeasonStats[] }) {
  const awarded = stats.filter(s => s.player_of_match > 0).sort((a, b) => b.player_of_match - a.player_of_match);
  return (
    <table className="w-full text-xs">
      <thead className="bg-[#0b5c33]/40">
        <tr className="text-green-300/70">
          <th className="px-3 py-2.5 text-left font-semibold">#</th>
          <th className="px-3 py-2.5 text-left font-semibold">Player</th>
          <th className="px-3 py-2.5 text-center font-semibold">Player of the Match</th>
        </tr>
      </thead>
      <tbody>
        {awarded.map((s, i) => (
          <tr key={s.id || i} className="border-t border-[#0b5c33]/20 hover:bg-[#0b5c33]/10">
            <td className={`px-3 py-2 font-bold ${i === 0 ? "text-yellow-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-300" : "text-green-400"}`}>{i + 1}</td>
            <td className="px-3 py-2 text-white font-medium">{s.player_name}</td>
            <td className="px-3 py-2 text-center text-[#0f9d3d] font-bold">{s.player_of_match}</td>
          </tr>
        ))}
        {awarded.length === 0 && (
          <tr><td colSpan={3} className="px-4 py-6 text-center text-green-300/50">No awards recorded yet</td></tr>
        )}
      </tbody>
    </table>
  );
}
